import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const loadTexture = (artworkUrl, onLoad) => {
  if (!artworkUrl) {
    onLoad(null);
    return;
  }
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      onLoad(null);
      return;
    }
    ctx.drawImage(image, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
      if (r > 240 && g > 240 && b > 240) {
        data[i + 3] = 0;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    onLoad(texture);
  };
  image.src = artworkUrl;
};

export const BoxPreview = ({ dimensions, material, artworkUrl, artworkScale }) => {
  const mountRef = useRef(null);
  const boxRef = useRef(null);
  const planeRef = useRef(null);
  const planeBackRef = useRef(null);
  const lineTopRef = useRef(null);
  const lineBottomRef = useRef(null);
  const textureRef = useRef(null);
  const [textureAspect, setTextureAspect] = useState(1);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#f7f4ee");

    const camera = new THREE.PerspectiveCamera(
      45,
      mount.clientWidth / mount.clientHeight,
      0.1,
      100,
    );
    camera.position.set(0, 0, 6);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    mount.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.1);
    directionalLight.position.set(3, 4, 6);
    scene.add(ambientLight, directionalLight);

    const boxGeometry = new THREE.BoxGeometry(2, 1.4, 1.2);
    const boxMaterial = new THREE.MeshStandardMaterial({ color: "#C4A484", roughness: 0.8 });
    const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial);
    boxMesh.rotation.set(0.15, -0.6, 0);
    scene.add(boxMesh);

    const planeGeometry = new THREE.PlaneGeometry(1, 1);
    const planeMaterial = new THREE.MeshStandardMaterial({ transparent: true });
    const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
    planeMesh.position.set(0, 0, 0.8);
    boxMesh.add(planeMesh);

    const backPlaneGeometry = new THREE.PlaneGeometry(1, 1);
    const backPlaneMaterial = new THREE.MeshStandardMaterial({ transparent: true });
    const backPlaneMesh = new THREE.Mesh(backPlaneGeometry, backPlaneMaterial);
    backPlaneMesh.position.set(0, 0, -0.8);
    backPlaneMesh.rotation.y = Math.PI;
    boxMesh.add(backPlaneMesh);

    const lineMaterial = new THREE.MeshBasicMaterial({ color: 0x111111, side: THREE.DoubleSide });
    const topLineGeometry = new THREE.BoxGeometry(1, 0.003, 0.002);
    const bottomLineGeometry = new THREE.BoxGeometry(1, 0.003, 0.002);
    const topLine = new THREE.Mesh(topLineGeometry, lineMaterial);
    const bottomLine = new THREE.Mesh(bottomLineGeometry, lineMaterial);
    boxMesh.add(topLine);
    boxMesh.add(bottomLine);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.enableDamping = true;
    controls.target.set(0, 0, 0);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    boxRef.current = boxMesh;
    planeRef.current = planeMesh;
    planeBackRef.current = backPlaneMesh;
    lineTopRef.current = topLine;
    lineBottomRef.current = bottomLine;

    let frameId;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(frameId);
      controls.dispose();
      renderer.dispose();
      boxGeometry.dispose();
      boxMaterial.dispose();
      planeGeometry.dispose();
      planeMaterial.dispose();
      backPlaneGeometry.dispose();
      backPlaneMaterial.dispose();
      topLineGeometry.dispose();
      bottomLineGeometry.dispose();
      lineMaterial.dispose();
      if (textureRef.current) textureRef.current.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  useEffect(() => {
    if (!boxRef.current || !planeRef.current || !planeBackRef.current) return;
    const scaleFactor = 0.2;
    const dims = {
      x: Math.max(1.4, dimensions.length * scaleFactor),
      y: Math.max(1, dimensions.height * scaleFactor),
      z: Math.max(1.1, dimensions.width * scaleFactor),
    };
    const materialColor = material === "White" ? "#f5f5f5" : "#C4A484";
    const maxDecalWidth = Math.max(0.5, dimensions.length - 1) * scaleFactor;
    const maxDecalHeight = Math.max(0.5, dimensions.height - 1) * scaleFactor;
    const safeAspect = textureAspect || 1;
    const maxWidthForAspect = Math.min(maxDecalWidth, maxDecalHeight * safeAspect);
    const maxHeightForAspect = Math.min(maxDecalHeight, maxDecalWidth / safeAspect);
    const decalWidth = Math.max(0.2, maxWidthForAspect * artworkScale);
    const decalHeight = Math.max(0.2, maxHeightForAspect * artworkScale);

    boxRef.current.geometry.dispose();
    boxRef.current.geometry = new THREE.BoxGeometry(dims.x, dims.y, dims.z);
    boxRef.current.material.color.set(materialColor);

    planeRef.current.geometry.dispose();
    planeBackRef.current.geometry.dispose();
    planeRef.current.geometry = new THREE.PlaneGeometry(decalWidth, decalHeight);
    planeBackRef.current.geometry = new THREE.PlaneGeometry(decalWidth, decalHeight);
    planeRef.current.position.set(0, 0, dims.z / 2 + 0.02);
    planeBackRef.current.position.set(0, 0, -(dims.z / 2 + 0.02));

    if (lineTopRef.current && lineBottomRef.current) {
      const seamHeight = Math.max(0.002, dims.y * 0.006);
      const seamDepth = Math.max(0.002, dims.z * 0.004);
      lineTopRef.current.geometry.dispose();
      lineBottomRef.current.geometry.dispose();
      lineTopRef.current.geometry = new THREE.BoxGeometry(dims.x, seamHeight, seamDepth);
      lineBottomRef.current.geometry = new THREE.BoxGeometry(dims.x, seamHeight, seamDepth);
      lineTopRef.current.position.set(0, dims.y / 2 + seamHeight / 2, 0);
      lineBottomRef.current.position.set(0, -dims.y / 2 - seamHeight / 2, 0);
    }

    loadTexture(artworkUrl, (texture) => {
      if (textureRef.current) textureRef.current.dispose();
      textureRef.current = texture;
      if (texture?.image?.width && texture?.image?.height) {
        const nextAspect = texture.image.width / texture.image.height;
        setTextureAspect((prev) => (Math.abs(prev - nextAspect) < 0.001 ? prev : nextAspect));
      }
      planeRef.current.material.map = texture;
      planeBackRef.current.material.map = texture;
      planeRef.current.material.needsUpdate = true;
      planeBackRef.current.material.needsUpdate = true;
      planeRef.current.visible = Boolean(texture);
      planeBackRef.current.visible = Boolean(texture);
    });
  }, [dimensions, material, artworkUrl, artworkScale, textureAspect]);

  return (
    <div className="relative h-full w-full" data-testid="box-preview-container">
      <div ref={mountRef} className="h-full w-full" data-testid="box-preview-canvas" />
      {!artworkUrl ? (
        <div
          className="pointer-events-none absolute inset-x-6 bottom-6 rounded-none border-2 border-dashed border-black bg-white/90 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-primary"
          data-testid="box-preview-placeholder"
        >
          Upload artwork to preview placement
        </div>
      ) : null}
    </div>
  );
};